import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Loader2, Copy, Check, Mail, Plus, X, Linkedin, Twitter, Trash2,
} from "lucide-react";

/**
 * ShareLinkDialog
 *
 * Owner-side modal that:
 *   1. Mints (or fetches) a public share token for the given item.
 *   2. Lets the owner copy the link, email it to N recipients, or share
 *      to LinkedIn / X.
 *   3. Lets the owner revoke the link.
 *
 * Email-gated viewer flow + soft CTA + non-watermarked carousel all live
 * on the /share/:token page (PublicSharePage).
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - item: { id, _type | type } where _type ∈ { "slide", "deck" }. Falls
 *     back to inferring kind from `slide_thumbnails` presence.
 */
export default function ShareLinkDialog({ open, onClose, item }) {
  const [link, setLink] = useState(null);  // { token, url, view_count, ... }
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [copied, setCopied] = useState(false);
  const [emails, setEmails] = useState([""]);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const kind =
    item?._type === "deck" || item?.type === "deck" || item?.num_slides > 0 || (item?.slide_thumbnails?.length || 0) > 1
      ? "deck"
      : "slide";

  // Mint or fetch the link when the dialog opens.
  useEffect(() => {
    if (!open || !item?.id) return;
    let cancelled = false;
    (async () => {
      setCreating(true);
      try {
        // Try fetching existing link first (cheaper, no insert on idempotent re-open).
        let res;
        try {
          res = await axios.get(
            `${API}/share/links/by-item/${kind}/${item.id}`,
            { withCredentials: true },
          );
        } catch (err) {
          if (err.response?.status !== 404) throw err;
          res = await axios.post(
            `${API}/share/links`,
            { kind, item_id: item.id },
            { withCredentials: true },
          );
        }
        if (!cancelled) setLink(res.data);
      } catch (err) {
        if (!cancelled) {
          toast.error(err.response?.data?.detail || "Couldn't create share link");
          onClose?.();
        }
      } finally {
        if (!cancelled) setCreating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, item?.id, kind]);  // eslint-disable-line

  // Reset transient state when the dialog closes.
  useEffect(() => {
    if (!open) {
      setLink(null);
      setEmails([""]);
      setNote("");
      setCopied(false);
    }
  }, [open]);

  const handleCopy = async () => {
    if (!link?.url) return;
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — please copy manually.");
    }
  };

  const handleRevoke = async () => {
    if (!link?.token) return;
    setRevoking(true);
    try {
      await axios.post(
        `${API}/share/links/${link.token}/revoke`, {},
        { withCredentials: true },
      );
      toast.success("Share link revoked");
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't revoke link");
    } finally { setRevoking(false); }
  };

  const handleSendEmail = async () => {
    const cleaned = emails.map(e => e.trim()).filter(Boolean);
    if (!cleaned.length) {
      toast.error("Add at least one email");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/share/links/${link.token}/email`,
        { recipients: cleaned, note: note || undefined },
        { withCredentials: true },
      );
      toast.success(`Sent to ${res.data.sent} of ${res.data.total}`);
      setEmails([""]);
      setNote("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send email");
    } finally { setSending(false); }
  };

  const handleSocial = (platform) => {
    if (!link?.url) return;
    const txt = encodeURIComponent("Check out this presentation");
    const url = encodeURIComponent(link.url);
    const target =
      platform === "linkedin"
        ? `https://www.linkedin.com/sharing/share-offsite/?url=${url}`
        : `https://twitter.com/intent/tweet?text=${txt}&url=${url}`;
    window.open(target, "_blank", "noopener,width=600,height=600");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="share-link-dialog">
        <DialogHeader>
          <DialogTitle>Share via link</DialogTitle>
          <DialogDescription className="text-xs">
            Anyone with the link enters their email before they can preview & download.
          </DialogDescription>
        </DialogHeader>

        {creating || !link ? (
          <div className="py-10 flex justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-5 pt-1">
            {/* Copy link */}
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1.5 block">Public link</label>
              <div className="flex gap-2">
                <Input
                  readOnly value={link.url}
                  className="flex-1 font-mono text-xs h-9"
                  data-testid="share-link-url"
                  onClick={(e) => e.target.select()}
                />
                <Button
                  onClick={handleCopy}
                  variant="outline" size="sm"
                  className="h-9"
                  data-testid="share-link-copy-btn"
                >
                  {copied ? <Check className="w-4 h-4 text-teal-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              {(link.view_count > 0 || link.viewer_count > 0) && (
                <p className="text-[11px] text-gray-400 mt-1.5">
                  {link.viewer_count || 0} viewer{(link.viewer_count || 0) === 1 ? "" : "s"} · {link.download_count || 0} download{(link.download_count || 0) === 1 ? "" : "s"}
                </p>
              )}
            </div>

            {/* Social share */}
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1.5 block">Share on social</label>
              <div className="flex gap-2">
                <Button
                  variant="outline" size="sm" onClick={() => handleSocial("linkedin")}
                  className="flex-1 h-9 text-xs"
                  data-testid="share-link-linkedin-btn"
                >
                  <Linkedin className="w-3.5 h-3.5 mr-1.5" />LinkedIn
                </Button>
                <Button
                  variant="outline" size="sm" onClick={() => handleSocial("twitter")}
                  className="flex-1 h-9 text-xs"
                  data-testid="share-link-twitter-btn"
                >
                  <Twitter className="w-3.5 h-3.5 mr-1.5" />X / Twitter
                </Button>
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1.5 block flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" /> Send by email
              </label>
              <div className="space-y-1.5">
                {emails.map((em, i) => (
                  <div key={i} className="flex gap-1.5">
                    <Input
                      type="email"
                      placeholder="recipient@example.com"
                      value={em}
                      onChange={(e) => {
                        const next = [...emails];
                        next[i] = e.target.value;
                        setEmails(next);
                      }}
                      className="flex-1 h-9 text-sm"
                      data-testid={`share-link-email-${i}`}
                    />
                    {emails.length > 1 && (
                      <Button
                        size="sm" variant="ghost" className="h-9 w-9 p-0 text-gray-400 hover:text-red-500"
                        onClick={() => setEmails(emails.filter((_, j) => j !== i))}
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setEmails([...emails, ""])}
                className="mt-1.5 text-xs text-teal-600 hover:text-teal-700 inline-flex items-center gap-1"
                data-testid="share-link-add-email-btn"
              >
                <Plus className="w-3 h-3" /> Add another
              </button>
              <Textarea
                placeholder="Add a note (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="mt-2 text-sm min-h-[60px]"
                data-testid="share-link-note"
              />
              <Button
                onClick={handleSendEmail}
                disabled={sending}
                className="w-full mt-2 bg-black hover:bg-gray-800 h-9"
                data-testid="share-link-send-email-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send email"}
              </Button>
            </div>

            {/* Revoke */}
            <div className="pt-3 border-t border-gray-100 flex justify-end">
              <Button
                variant="ghost" size="sm" onClick={handleRevoke} disabled={revoking}
                className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                data-testid="share-link-revoke-btn"
              >
                {revoking ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <><Trash2 className="w-3.5 h-3.5 mr-1" />Revoke link</>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
